import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Plus, X, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { damageNoteService } from '../../services/damageNoteService';
import { apiService } from '../../services/api';
import { CreateDamageNoteRequest } from '../../types';
import './DamageNotePage.css';
import { parseApiError } from '../../utils/errorUtils';

// ─── Local types ──────────────────────────────────────────────────────────────

interface WarehouseOption { id: number; name: string; }
interface ProductOption   { id: number; name: string; sku: string; }
interface UnitOption      { id: number; unitName: string; }

interface LineItem {
  productId:      number;
  unitId:         number;
  quantity:       number;
  damageCategory: string;
  estimatedValue: number;
  reasonNotes:    string;
}

const DAMAGE_CATEGORIES = [
  'Breakage',
  'Expiry',
  'Spoilage',
  'Leakage',
  'OperationalDamage',
] as const;

const DAMAGE_CATEGORY_LABELS: Record<string, string> = {
  Breakage:          'Breakage',
  Expiry:            'Expiry',
  Spoilage:          'Spoilage',
  Leakage:           'Leakage',
  OperationalDamage: 'Operational Damage',
};

const emptyLine = (): LineItem => ({
  productId:      0,
  unitId:         0,
  quantity:       0,
  damageCategory: '',
  estimatedValue: 0,
  reasonNotes:    '',
});

// ─── Component ────────────────────────────────────────────────────────────────

const DamageNoteCreatePage: React.FC = () => {
  const navigate  = useNavigate();
  const { id }    = useParams<{ id?: string }>();
  const { t }     = useTranslation();
  const { user }  = useAuth();
  const isEdit    = !!id;

  // ── Header fields ──────────────────────────────────────────────────────────
  const [warehouseId,  setWarehouseId]  = useState<number>(0);
  const [damageDate,   setDamageDate]   = useState(new Date().toISOString().split('T')[0]);
  const [notes,        setNotes]        = useState('');

  // ── Line items ─────────────────────────────────────────────────────────────
  const [items, setItems] = useState<LineItem[]>([emptyLine()]);

  // ── Dropdown data ──────────────────────────────────────────────────────────
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [products,   setProducts]   = useState<ProductOption[]>([]);
  const [units,      setUnits]      = useState<UnitOption[]>([]);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [loading,     setLoading]     = useState(false);
  const [initialLoad, setInitialLoad] = useState(isEdit);
  const [error,       setError]       = useState<string | null>(null);

  // ── Load dropdowns (+ edit data) ──────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        // Warehouses live under api/v1/organization/warehouses — the short
        // /warehouses path 404s. Products and units use api/v1/[controller],
        // so their short paths resolve correctly.
        const [whRes, prodRes, unitRes] = await Promise.all([
          apiService.get<unknown>('/organization/warehouses', { pageSize: 200 }),
          apiService.get<unknown>('/products',    { pageSize: 1000, isActive: true }),
          apiService.get<unknown>('/units',       { pageSize: 200 }),
        ]);

        const mapWh   = (d: unknown): WarehouseOption[] => {
          const list = (d as { data?: { data?: unknown[]; }; })?.data?.data
            ?? (d as { data?: unknown[]; })?.data ?? [];
          return (list as Record<string, unknown>[]).map((w) => ({
            id:   Number(w.id   ?? w.warehouseId),
            name: String(w.name ?? w.warehouseName),
          }));
        };

        const mapProd = (d: unknown): ProductOption[] => {
          const list = (d as { data?: { data?: unknown[]; }; })?.data?.data
            ?? (d as { data?: unknown[]; })?.data ?? [];
          return (list as Record<string, unknown>[]).map((p) => ({
            id:  Number(p.id   ?? p.productId),
            name: String(p.name ?? p.productName),
            sku: String(p.sku  ?? p.productCode ?? ''),
          }));
        };

        const mapUnit = (d: unknown): UnitOption[] => {
          const list = (d as { data?: { data?: unknown[]; }; })?.data?.data
            ?? (d as { data?: unknown[]; })?.data ?? [];
          return (list as Record<string, unknown>[]).map((u) => ({
            id:       Number(u.id ?? u.unitId),
            unitName: String(u.unitName ?? u.name),
          }));
        };

        setWarehouses(mapWh(whRes.data));
        setProducts(mapProd(prodRes.data));
        setUnits(mapUnit(unitRes.data));

        if (isEdit && id) {
          const dnRes = await damageNoteService.getById(id);
          const dn    = dnRes.data?.data;
          if (dn) {
            setWarehouseId(dn.warehouseId);
            setDamageDate(dn.damageDate.split('T')[0]);
            setNotes(dn.notes ?? '');
            if (dn.items?.length) {
              setItems(
                dn.items.map((item) => ({
                  productId:      item.productId,
                  unitId:         item.unitId,
                  quantity:       item.quantity,
                  damageCategory: item.damageCategory,
                  estimatedValue: item.estimatedValue,
                  reasonNotes:    item.reasonNotes ?? '',
                }))
              );
            }
          }
        }
      } catch {
        setError(t('damageNote.failedToLoad'));
      } finally {
        setInitialLoad(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Item handlers ──────────────────────────────────────────────────────────

  const handleItemChange = (index: number, field: keyof LineItem, value: unknown) => {
    setItems((prev) => {
      const next = [...prev];
      (next[index] as Record<string, unknown>)[field] = value;
      return next;
    });
  };

  const addLine    = () => setItems((prev) => [...prev, emptyLine()]);
  const removeLine = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  // ── Computed summary ───────────────────────────────────────────────────────

  const validItems       = items.filter((i) => i.productId > 0 && i.unitId > 0 && i.quantity > 0);
  const totalEstimated   = items.reduce((sum, i) => sum + (i.estimatedValue || 0), 0);

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = (): string | null => {
    if (warehouseId <= 0)     return t('damageNote.warehouseRequired');
    if (!damageDate)          return t('damageNote.dateRequired');
    if (validItems.length === 0) return t('damageNote.itemsRequired');
    return null;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError(null);

    try {
      if (isEdit && id) {
        await damageNoteService.update(id, {
          warehouseId,
          damageDate,
          notes:             notes || undefined,
          reportedByUserId:  user?.id ?? 0,
          items:             validItems.map((i) => ({
            productId:      i.productId,
            unitId:         i.unitId,
            quantity:       i.quantity,
            damageCategory: i.damageCategory,
            estimatedValue: i.estimatedValue,
            reasonNotes:    i.reasonNotes || undefined,
          })),
        });
        navigate(`/damage-notes/${id}`, {
          state: { message: t('damageNote.updatedSuccess') },
        });
      } else {
        const payload: CreateDamageNoteRequest = {
          warehouseId,
          damageDate,
          notes:            notes || undefined,
          reportedByUserId: user?.id ?? 0,
          items:            validItems.map((i) => ({
            productId:      i.productId,
            unitId:         i.unitId,
            quantity:       i.quantity,
            damageCategory: i.damageCategory,
            estimatedValue: i.estimatedValue,
            reasonNotes:    i.reasonNotes || undefined,
          })),
        };
        const res     = await damageNoteService.create(payload);
        const created = res.data?.data;
        navigate(`/damage-notes/${created?.id}`, {
          state: { message: t('damageNote.createdSuccess', { number: created?.damageNoteNumber }) },
        });
      }
    } catch (err: unknown) {
      setError(parseApiError(err, isEdit ? t('damageNote.failedToUpdate') : t('damageNote.failedToCreate')).message);
    } finally {
      setLoading(false);
    }
  };

  // ── Loading guard ──────────────────────────────────────────────────────────

  if (initialLoad) {
    return (
      <Layout>
        <div
          className="ims-page dn-page"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}
        >
          <div className="ims-spinner" />
        </div>
      </Layout>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="ims-create-body-page dn-page">

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button
              type="button"
              className="ims-back-btn"
              onClick={() => navigate('/damage-notes')}
            >
              <ArrowLeft size={13} strokeWidth={2.5} />
              {t('damageNote.backToList')}
            </button>
            <div>
              <h1 className="ims-page-title">
                {isEdit ? t('damageNote.editTitle') : t('damageNote.createTitle')}
              </h1>
              <p className="ims-page-subtitle">
                {isEdit ? t('damageNote.editSubtitle') : t('damageNote.createSubtitle')}
              </p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/damage-notes')}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? (
                <><span className="ims-btn-spinner" />{t('common.saving')}</>
              ) : (
                <>
                  <Save size={14} strokeWidth={2} />
                  {isEdit ? t('common.saveChanges') : t('damageNote.createDamageNote')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            <AlertCircle size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* ─── LEFT — Damage Note Details ─── */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('damageNote.damageNoteDetails')}</h2>
              </div>
              <div className="ims-card-body">

                {/* Warehouse */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('damageNote.warehouse')} <span className="required">*</span>
                  </label>
                  <select
                    className="ims-form-control"
                    aria-label={t('damageNote.warehouse')}
                    value={warehouseId}
                    onChange={(e) => setWarehouseId(Number(e.target.value))}
                  >
                    <option value={0}>{t('damageNote.selectWarehouse')}</option>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>

                {/* Damage Date */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('damageNote.damageDate')} <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    id="dn-damage-date"
                    className="ims-form-control"
                    aria-label={t('damageNote.damageDate')}
                    value={damageDate}
                    onChange={(e) => setDamageDate(e.target.value)}
                    required
                  />
                </div>

                {/* Notes */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('common.notes')}</label>
                  <textarea
                    className="ims-form-control ims-textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder={t('damageNote.notesPlaceholder')}
                  />
                </div>

              </div>
            </div>
          </div>

          {/* ─── CENTER — Damaged Items ─── */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('damageNote.damagedItems')}</h2>
                <button
                  type="button"
                  className="ims-btn ims-btn-secondary"
                  style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={addLine}
                >
                  <Plus size={12} strokeWidth={2.5} />
                  {t('damageNote.addItem')}
                </button>
              </div>

              <div className="ims-lines-body">
                {items.length === 0 ? (
                  <div className="ims-items-empty">
                    <div className="ims-items-empty-icon">⚠️</div>
                    {t('damageNote.addItemsHint')}
                  </div>
                ) : (
                  <div className="ims-table-scroll">
                    <table className="ims-table">
                      <thead>
                        <tr>
                          <th className="ims-th" style={{ minWidth: 200 }}>{t('damageNote.product')}</th>
                          <th className="ims-th" style={{ minWidth: 120 }}>{t('damageNote.unit')}</th>
                          <th className="ims-th ims-th--num" style={{ minWidth: 100 }}>{t('damageNote.quantity')}</th>
                          <th className="ims-th" style={{ minWidth: 160 }}>{t('damageNote.damageCategory')}</th>
                          <th className="ims-th ims-th--num" style={{ minWidth: 130 }}>{t('damageNote.estimatedValue')}</th>
                          <th className="ims-th" style={{ minWidth: 180 }}>{t('damageNote.reasonNotes')}</th>
                          <th className="ims-th ims-th--del" />
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={index} className="ims-tr">
                            {/* Product */}
                            <td className="ims-td">
                              <select
                                className="ims-cell-input"
                                aria-label={t('damageNote.product')}
                                value={item.productId}
                                onChange={(e) => handleItemChange(index, 'productId', Number(e.target.value))}
                              >
                                <option value={0}>{t('damageNote.selectProduct')}</option>
                                {products.map((p) => (
                                  <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                                ))}
                              </select>
                            </td>
                            {/* Unit */}
                            <td className="ims-td">
                              <select
                                className="ims-cell-input"
                                aria-label={t('damageNote.unit')}
                                value={item.unitId}
                                onChange={(e) => handleItemChange(index, 'unitId', Number(e.target.value))}
                              >
                                <option value={0}>{t('damageNote.selectUnit')}</option>
                                {units.map((u) => (
                                  <option key={u.id} value={u.id}>{u.unitName}</option>
                                ))}
                              </select>
                            </td>
                            {/* Quantity */}
                            <td className="ims-td ims-td--num">
                              <input
                                type="number"
                                className="ims-cell-input ims-num-input"
                                aria-label={t('damageNote.quantity')}
                                step="0.01"
                                min={0.01}
                                value={item.quantity || ''}
                                onChange={(e) =>
                                  handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)
                                }
                                placeholder="0"
                              />
                            </td>
                            {/* Damage Category */}
                            <td className="ims-td">
                              <select
                                className="ims-cell-input"
                                value={item.damageCategory}
                                onChange={(e) => handleItemChange(index, 'damageCategory', e.target.value)}
                              >
                                <option value="">{t('damageNote.selectCategory')}</option>
                                {DAMAGE_CATEGORIES.map((cat) => (
                                  <option key={cat} value={cat}>{DAMAGE_CATEGORY_LABELS[cat]}</option>
                                ))}
                              </select>
                            </td>
                            {/* Estimated Value */}
                            <td className="ims-td ims-td--num">
                              <input
                                type="number"
                                className="ims-cell-input ims-num-input"
                                step="0.01"
                                min={0}
                                value={item.estimatedValue || ''}
                                onChange={(e) =>
                                  handleItemChange(index, 'estimatedValue', parseFloat(e.target.value) || 0)
                                }
                                placeholder="0.00"
                              />
                            </td>
                            {/* Reason Notes */}
                            <td className="ims-td">
                              <input
                                type="text"
                                className="ims-cell-input"
                                value={item.reasonNotes}
                                onChange={(e) => handleItemChange(index, 'reasonNotes', e.target.value)}
                                placeholder={t('damageNote.reasonNotesPlaceholder')}
                              />
                            </td>
                            {/* Remove */}
                            <td className="ims-td ims-td--del">
                              <button
                                type="button"
                                className="ims-remove-btn"
                                onClick={() => removeLine(index)}
                                disabled={items.length === 1}
                                title={t('common.remove')}
                              >
                                <X size={12} strokeWidth={2.5} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ─── RIGHT — Summary + Reported By ─── */}
          <div className="ims-panel-right">

            {/* Reported By */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('damageNote.reportedBy')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-user-row">
                  <div className="ims-user-avatar">
                    {user?.firstName?.[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div>
                    <div className="ims-user-name">{user?.firstName} {user?.lastName}</div>
                    <div className="ims-user-email">{user?.email}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('damageNote.summary')}</h3>
              </div>
              <div className="ims-card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--ims-text-sub)' }}>
                      {t('damageNote.totalItems')}
                    </span>
                    <span style={{ fontWeight: 700, color: 'var(--ims-text-main)' }}>
                      {validItems.length}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: '#fee2e2',
                      border: '1px solid #fecaca',
                    }}
                  >
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#991b1b' }}>
                      {t('damageNote.totalEstimatedValue')}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: '1rem', color: '#7f1d1d' }}>
                      {totalEstimated.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('damageNote.tipsTitle')}</h3>
              </div>
              <div className="ims-card-body">
                <ul className="ims-help-list">
                  <li>{t('damageNote.tip1')}</li>
                  <li>{t('damageNote.tip2')}</li>
                  <li>{t('damageNote.tip3')}</li>
                </ul>
              </div>
            </div>

          </div>

        </div>
      </form>
    </Layout>
  );
};

export default DamageNoteCreatePage;
